import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, useAuth, formatApiError } from "../context/AuthContext";
import { IndosbookLogo } from "./IndosbookLogo";
import { Button } from "./ui/button";
import { Ship, GraduationCap, MapPin, Users, Anchor, Wallet, Check, Loader2, ArrowLeft, CalendarDays } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const Fact = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-slate-600"><Icon className="h-4 w-4 text-slate-400" /> {children}</span>
);

const JobCard = ({ j, onApply, busy }) => (
  <article className="rounded-2xl border border-stone-200 bg-white p-5 transition-colors hover:border-orange-200" data-testid={`org-job-${j.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="font-heading text-base font-bold text-slate-900">{j.title}</h3>
        <p className="mt-0.5 text-xs text-slate-400">Posted {j.time === "just now" ? "just now" : `${j.time} ago`}</p>
      </div>
      {j.applied ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid={`org-job-applied-${j.id}`}><Check className="h-3.5 w-3.5" /> Applied</span>
      ) : (
        <Button onClick={() => onApply(j)} disabled={busy} data-testid={`org-job-apply-${j.id}`} className="h-9 rounded-full bg-[#FF4500] px-5 text-xs font-bold text-white hover:bg-[#e03d00]">Apply</Button>
      )}
    </div>
    {j.body && <p className="mt-3 text-sm leading-relaxed text-slate-600">{j.body}</p>}
    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-stone-100 pt-4">
      {j.job?.rank && <Fact icon={Anchor}>{j.job.rank}</Fact>}
      {j.job?.vessel && <Fact icon={Ship}>{j.job.vessel}</Fact>}
      {j.job?.salary && <Fact icon={Wallet}>{j.job.salary}</Fact>}
      {j.job?.location && <Fact icon={MapPin}>{j.job.location}</Fact>}
    </div>
  </article>
);

export default function OrgPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await api.get(`/orgs/${id}`)).data); }
    catch (e) { setError(formatApiError(e?.response?.data?.detail, "Page not found")); }
  }, [id]);
  useEffect(() => { window.scrollTo(0, 0); load(); }, [load]);

  const toSignup = () => navigate("/signup?role=seafarer");

  const toggleFollow = async () => {
    if (!user) return toSignup();
    setBusy(true);
    try {
      const f = data.org.following;
      await api.post(`/companies/${id}/${f ? "unfollow" : "follow"}`);
      setData((d) => ({ ...d, org: { ...d.org, following: !f, followers: d.org.followers + (f ? -1 : 1) } }));
    } catch { toast({ title: "Action failed", variant: "destructive" }); } finally { setBusy(false); }
  };

  const apply = async (j) => {
    if (!user) return toSignup();
    if (user.role !== "seafarer") return toast({ title: "Only seafarer accounts can apply to jobs" });
    setBusy(true);
    try {
      await api.post("/applications", { job_post_id: j.id, job_title: j.title, company: data.org.name, rank: j.job?.rank, vessel: j.job?.vessel, salary: j.job?.salary, location: j.job?.location });
      setData((d) => ({ ...d, jobs: d.jobs.map((x) => (x.id === j.id ? { ...x, applied: true } : x)) }));
      toast({ title: "Application submitted!", description: `${j.title} · ${data.org.name}` });
    } catch (e) { toast({ title: "Could not apply", description: formatApiError(e?.response?.data?.detail), variant: "destructive" }); } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white" data-testid="org-not-found">
        <p className="text-lg font-semibold text-slate-700">{error}</p>
        <Link to="/" className="font-bold text-[#FF4500] hover:underline">Back to home</Link>
      </div>
    );
  }
  if (!data) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { org, jobs } = data;
  const isInst = org.role === "institute";
  const Icon = isInst ? GraduationCap : Ship;
  const avatar = org.avatar_path ? `${API_BASE}/files/${org.avatar_path}` : org.picture;

  return (
    <div className="min-h-screen bg-white" data-testid="org-page">
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[1000px] items-center justify-between px-4 sm:px-6">
          <Link to={user ? "/feed" : "/"}><IndosbookLogo size={28} /></Link>
          {user ? (
            <Link to="/feed" className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#FF4500]"><ArrowLeft className="h-4 w-4" /> Back to feed</Link>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate("/login")} className="rounded-full font-bold text-[#FF4500] hover:bg-[#FF4500]/10">Log In</Button>
              <Button onClick={() => navigate("/signup")} className="rounded-full bg-[#FF4500] font-bold text-white hover:bg-[#e03d00]">Sign Up</Button>
            </div>
          )}
        </div>
      </header>

      <section className="indos-hero-mesh indos-grid-pattern relative overflow-hidden border-b border-stone-200/80">
        <div className="relative mx-auto flex max-w-[1000px] flex-col gap-6 px-4 py-12 sm:flex-row sm:items-center sm:px-6 md:py-16">
          {avatar ? <img src={avatar} alt="" className="h-24 w-24 shrink-0 rounded-3xl border border-stone-200 bg-white object-cover" />
            : <span className="flex h-24 w-24 shrink-0 items-center justify-center rounded-3xl bg-[#002855] text-white"><Icon className="h-10 w-10" /></span>}
          <div className="min-w-0 flex-1">
            <span className="text-xs font-bold uppercase tracking-wider text-[#FF4500]">{isInst ? "Maritime Institute" : "Shipping Company"}</span>
            <h1 className="font-heading mt-1 text-3xl font-extrabold text-slate-950 sm:text-4xl" data-testid="org-name">{org.name}</h1>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              <Fact icon={Users}><span data-testid="org-followers">{org.followers}</span> follower{org.followers === 1 ? "" : "s"}</Fact>
              {org.location && <Fact icon={MapPin}>{org.location}</Fact>}
              {org.member_since && <Fact icon={CalendarDays}>On Indosbook since {new Date(org.member_since).toLocaleDateString(undefined, { month: "short", year: "numeric" })}</Fact>}
            </div>
          </div>
          {!org.is_me && (
            <Button onClick={toggleFollow} disabled={busy} data-testid="org-follow-button"
              className={`h-11 rounded-full px-6 font-bold ${org.following ? "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" : "bg-[#FF4500] text-white hover:bg-[#e03d00]"}`}>
              {org.following ? <><Check className="mr-1.5 h-4 w-4" /> Following</> : "Follow"}
            </Button>
          )}
        </div>
      </section>

      <main className="mx-auto grid max-w-[1000px] gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_300px]">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900">{isInst ? "Courses & announcements" : "Open vacancies"} <span className="text-slate-400">({jobs.length})</span></h2>
          <div className="mt-4 space-y-4">
            {jobs.length === 0 && <p className="rounded-2xl border border-dashed border-stone-300 p-8 text-center text-sm text-slate-500" data-testid="org-jobs-empty">No open {isInst ? "listings" : "vacancies"} right now. Follow to get notified first.</p>}
            {jobs.map((j) => <JobCard key={j.id} j={j} onApply={apply} busy={busy} />)}
          </div>
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl border border-stone-200 bg-white p-5">
            <h3 className="font-heading text-sm font-bold text-slate-900">About</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="org-bio">{org.bio || `${org.name} is on Indosbook — India's merchant navy network.`}</p>
          </div>
          {!user && (
            <div className="indos-grid-white relative overflow-hidden rounded-2xl bg-[#FF4500] p-5 text-white">
              <h3 className="font-heading relative text-base font-bold">Join Indosbook free</h3>
              <p className="relative mt-1 text-sm text-orange-50">Follow {org.name} and apply to their {isInst ? "courses" : "jobs"} in one tap.</p>
              <Button onClick={toSignup} data-testid="org-join-cta" className="relative mt-4 h-10 w-full rounded-full bg-white font-bold text-[#FF4500] hover:bg-orange-50">Create free account</Button>
            </div>
          )}
        </aside>
      </main>
    </div>
  );
}
